import type { ProductCategory } from '@/types/pos'

export interface CategoryTreeNode {
  category: ProductCategory
  children: CategoryTreeNode[]
}

function getCatId(c: ProductCategory): string {
  return c.id || (c as any)._id
}

/** Sort siblings by order (null last), then by name */
function sortSiblings(items: ProductCategory[]): ProductCategory[] {
  return [...items].sort((a, b) => {
    const oa = a.order != null ? a.order : 999999
    const ob = b.order != null ? b.order : 999999
    if (oa !== ob) return oa - ob
    return (a.name || '').localeCompare(b.name || '')
  })
}

/** Build a tree from flat categories. Roots and each level sorted by order then name. */
export function buildCategoryTree(categories: ProductCategory[]): CategoryTreeNode[] {
  const byParent = new Map<string | null, ProductCategory[]>()
  byParent.set(null, [])
  categories.forEach((c) => {
    const pid = c.parentId || null
    if (!byParent.has(pid)) byParent.set(pid, [])
    byParent.get(pid)!.push(c)
  })
  const sorted = new Map<string | null, ProductCategory[]>()
  byParent.forEach((arr, key) => { sorted.set(key, sortSiblings(arr)) })

  function build(parentId: string | null): CategoryTreeNode[] {
    const list = sorted.get(parentId) || []
    return list.map((cat) => ({
      category: cat,
      children: build(getCatId(cat)),
    }))
  }
  return build(null)
}

/** All descendant category IDs (recursive). */
export function getDescendantIds(categories: ProductCategory[], categoryId: string): string[] {
  const ids: string[] = []
  const children = categories.filter((c) => (c.parentId || '') === categoryId)
  children.forEach((c) => {
    const id = getCatId(c)
    ids.push(id, ...getDescendantIds(categories, id))
  })
  return ids
}

/** Flatten tree to list in display order (depth-first). */
export function flattenTree(nodes: CategoryTreeNode[], depth = 0): { node: CategoryTreeNode; depth: number }[] {
  const out: { node: CategoryTreeNode; depth: number }[] = []
  nodes.forEach((node) => {
    out.push({ node, depth })
    out.push(...flattenTree(node.children, depth + 1))
  })
  return out
}

/** Filter tree by search term: keep nodes whose name matches or that have a matching descendant. */
export function filterCategoryTree(nodes: CategoryTreeNode[], search: string): CategoryTreeNode[] {
  const q = search.trim().toLowerCase()
  if (!q) return nodes

  function matches(name: string): boolean {
    return name.toLowerCase().includes(q)
  }

  function filter(nodes: CategoryTreeNode[]): CategoryTreeNode[] {
    const result: CategoryTreeNode[] = []
    for (const node of nodes) {
      const childFiltered = filter(node.children)
      const nameMatch = matches(node.category.name || '')
      if (nameMatch || childFiltered.length > 0) {
        result.push({
          category: node.category,
          children: childFiltered,
        })
      }
    }
    return result
  }
  return filter(nodes)
}
